import type { IconName } from "../icons";
import type { PageId } from "../nav";

export type VerificationPrimaryNavId = "decide" | "define" | "evidence" | "prove" | "operate";

export type VerificationClientKey =
  | "overview"
  | "inbox"
  | "workLedger"
  | "workDetail"
  | "findingsList"
  | "findingsGet"
  | "findingsDecisions"
  | "findingsReview"
  | "runsList"
  | "runsGet"
  | "runsCreate"
  | "workflowsList"
  | "workflowsGet"
  | "workflowsIntake"
  | "workflowsAdmit"
  | "controlsList"
  | "controlsGet"
  | "policiesList"
  | "policiesGet"
  | "policyVersions"
  | "policiesActivate"
  | "artifactsList"
  | "artifactsGet"
  | "artifactsQueue"
  | "artifactsRedact"
  | "lineageGet"
  | "connectorsList"
  | "connectorsCatalog"
  | "connectorsReconnect"
  | "environmentsList"
  | "outcomesList"
  | "autonomyList"
  | "analyticsGet"
  | "receiptsList"
  | "receiptsGet"
  | "receiptsReplay"
  | "packsList"
  | "packsGet"
  | "packsCompose"
  | "packsDownload"
  | "auditRoomGet"
  | "auditRoomGrant"
  | "auditRoomRevoke"
  | "workersList"
  | "scorecardGet"
  | "runtimeGet"
  | "catalogGet"
  | "tokensCreate"
  | "tokensRevoke"
  | "tokensRotate"
  | "localEnvironmentsSpawn"
  | "localEnvironmentsReset"
  | "onboardingGet"
  | "onboardingFirstLoop"
  | "onboardingLocalModel"
  | "demoLab";

export interface CanonicalNavItem {
  page: PageId;
  path: string;
  label: string;
  icon: IconName;
  purpose: string;
  countTone?: "fail" | "unknown" | "accent";
  alsoActiveFor?: readonly PageId[];
}

export interface CanonicalNavGroup {
  id: VerificationPrimaryNavId;
  label: "Decide" | "Define" | "Evidence" | "Prove" | "Operate";
  rationale: string;
  items: readonly CanonicalNavItem[];
}

export interface CanonicalRouteSource {
  clientKey: VerificationClientKey;
  contract: string;
  operation: string;
  cacheScope: readonly ("tenant" | "environment" | "identity" | "role" | "filters" | "id")[];
  authoritativeFor: readonly (
    | "tenant"
    | "environment"
    | "identity"
    | "role"
    | "health"
    | "posture"
    | "inbox-count"
    | "search"
    | "verdict"
    | "receipt"
    | "work-detail"
  )[];
}

export interface CanonicalRoute {
  routeId: string;
  path: string;
  page: PageId;
  primary: VerificationPrimaryNavId;
  label: string;
  routeExport: string;
  pageRoot: string;
  source: CanonicalRouteSource;
  urlState?: readonly string[];
}

export const CANONICAL_NAV_GROUPS = [
  {
    id: "decide",
    label: "Decide",
    rationale:
      "Daily decision work: what needs action, what failed, and the runs that produced it.",
    items: [
      {
        page: "overview",
        path: "",
        label: "Overview",
        icon: "gauge",
        purpose: "Trust posture for the period, blockers, and what changed today.",
      },
      {
        page: "inbox",
        path: "inbox",
        label: "Inbox",
        icon: "inbox",
        purpose: "Actionable verification work assigned to you, consequence and deadline first.",
        countTone: "accent",
      },
      {
        page: "findings",
        path: "findings",
        label: "Findings",
        icon: "flag",
        purpose:
          "Every FAIL and UNKNOWN across the tenant, filterable by control, owner, severity, and age.",
        countTone: "fail",
        alsoActiveFor: ["finding"],
      },
      {
        page: "runs",
        path: "runs",
        label: "Runs",
        icon: "play",
        purpose: "Verification run history with claim, checks, evidence, receipt, and decisions.",
        alsoActiveFor: ["run"],
      },
    ],
  },
  {
    id: "define",
    label: "Define",
    rationale: "The live workflows and policy definitions that feed verification.",
    items: [
      {
        page: "work",
        path: "work-ledger",
        label: "Work ledger",
        icon: "chart",
        purpose:
          "Universal Work with completion-aware current authority and readable incomplete history.",
        countTone: "unknown",
        alsoActiveFor: ["work-detail"],
      },
      {
        page: "workflows",
        path: "workflows",
        label: "Workflows",
        icon: "link",
        purpose: "Connect a real workflow by naming the independent oracle that verifies it.",
        countTone: "unknown",
        alsoActiveFor: ["workflow", "workflow-new"],
      },
      {
        page: "controls",
        path: "controls",
        label: "Controls",
        icon: "shield",
        purpose:
          "The control library and its requirements: owner, frequency, population, current verdict.",
        alsoActiveFor: ["control"],
      },
      {
        page: "policies",
        path: "policies",
        label: "Policies",
        icon: "file",
        purpose: "The checkable logic behind each control, versioned and reviewed.",
        countTone: "unknown",
        alsoActiveFor: ["policy"],
      },
    ],
  },
  {
    id: "evidence",
    label: "Evidence",
    rationale:
      "Where the facts come from. Separated from Define because sources fail on their own schedule.",
    items: [
      {
        page: "evidence",
        path: "evidence",
        label: "Evidence",
        icon: "book",
        purpose: "Content-addressed artifacts and observations, with full lineage.",
        alsoActiveFor: ["artifact"],
      },
      {
        page: "connectors",
        path: "connectors",
        label: "Connectors",
        icon: "plug",
        purpose: "Read-only source connections, scopes, cadence, and freshness.",
        countTone: "fail",
      },
      {
        page: "environments",
        path: "environments",
        label: "Environments",
        icon: "server",
        purpose:
          "Signed Environment identities, generations, directory projections, and health posture.",
        countTone: "unknown",
      },
    ],
  },
  {
    id: "operate",
    label: "Operate",
    rationale: "The machinery's own health and trust posture.",
    items: [
      {
        page: "workers",
        path: "workers",
        label: "Workers",
        icon: "cpu",
        purpose:
          "Model-based checkers, calibration, and the boundary that prevents model-only PASS.",
      },
      {
        page: "scorecard",
        path: "scorecard",
        label: "Scorecard",
        icon: "chart",
        purpose:
          "The operating metrics that decide whether verification is trustworthy enough to sell on.",
      },
      {
        page: "runtime",
        path: "runtime",
        label: "Runtime & keys",
        icon: "server",
        purpose: "Where evidence lives, who holds signing keys, and whether offline replay works.",
      },
      {
        page: "api",
        path: "sdk",
        label: "API & SDK",
        icon: "code",
        purpose:
          "Served endpoints, scopes, and the operation deliberately withheld from API clients.",
      },
      {
        page: "demo-lab",
        path: "demo-lab",
        label: "Demo lab",
        icon: "check",
        purpose: "Spawn, mutate, and audit Demo and Product dogfood state through the product UI.",
      },
      {
        page: "trust",
        path: "trust",
        label: "Trust root",
        icon: "shield",
        purpose: "Out-of-band keys and chain anchoring an outside party can check.",
      },
      {
        page: "posture",
        path: "posture",
        label: "Posture",
        icon: "lock",
        purpose:
          "Enterprise security posture answered in the product, with unanswered rows visible.",
      },
      {
        page: "outcomes",
        path: "outcomes",
        label: "Outcomes",
        icon: "check",
        purpose:
          "Observed run outcomes, regressions, inconclusive checks, and receipt-backed follow-up.",
      },
      {
        page: "autonomy",
        path: "autonomy",
        label: "Autonomy",
        icon: "cpu",
        purpose:
          "Which workflows may act without a human, and the oracle boundaries that permit it.",
      },
      {
        page: "analytics",
        path: "analytics",
        label: "Analytics",
        icon: "chart",
        purpose:
          "Permission-safe rollups for verification health, outcome drift, and reviewer load.",
      },
    ],
  },
  {
    id: "prove",
    label: "Prove",
    rationale: "The output an outside party can inspect without trusting the app.",
    items: [
      {
        page: "receipts",
        path: "receipts",
        label: "Receipts",
        icon: "seal",
        purpose:
          "The append-only ledger binding each verdict to evidence, policy version, and signing key.",
        alsoActiveFor: ["receipt"],
      },
      {
        page: "packs",
        path: "packs",
        label: "Evidence packs",
        icon: "package",
        purpose: "Scoped, tamper-evident exports that replay offline and carry their scope limits.",
        alsoActiveFor: ["pack"],
      },
      {
        page: "audit-room",
        path: "audit-room",
        label: "Audit room",
        icon: "door",
        purpose: "Time-limited read-only reviewer access with view and download logging.",
      },
    ],
  },
] as const satisfies readonly CanonicalNavGroup[];

const shellSource = (
  clientKey: VerificationClientKey,
  operation: string,
  cacheScope: CanonicalRouteSource["cacheScope"],
  authoritativeFor: CanonicalRouteSource["authoritativeFor"],
): CanonicalRouteSource => ({
  clientKey,
  contract: "verificationShell",
  operation,
  cacheScope,
  authoritativeFor,
});

const defineSource = (
  clientKey: VerificationClientKey,
  operation: string,
  cacheScope: CanonicalRouteSource["cacheScope"],
): CanonicalRouteSource => ({
  clientKey,
  contract: "verificationDefine",
  operation,
  cacheScope,
  authoritativeFor: ["tenant", "environment", "identity", "role", "verdict"],
});

const evidenceSource = (
  clientKey: VerificationClientKey,
  operation: string,
  cacheScope: CanonicalRouteSource["cacheScope"],
): CanonicalRouteSource => ({
  clientKey,
  contract: "verificationEvidence",
  operation,
  cacheScope,
  authoritativeFor: ["tenant", "environment", "identity", "role", "search"],
});

const proveSource = (
  clientKey: VerificationClientKey,
  operation: string,
  cacheScope: CanonicalRouteSource["cacheScope"],
): CanonicalRouteSource => ({
  clientKey,
  contract: "verificationProve",
  operation,
  cacheScope,
  authoritativeFor: ["tenant", "environment", "identity", "role", "receipt"],
});

const operateSource = (
  clientKey: VerificationClientKey,
  operation: string,
  cacheScope: CanonicalRouteSource["cacheScope"],
  authoritativeFor: CanonicalRouteSource["authoritativeFor"] = [
    "tenant",
    "environment",
    "identity",
    "role",
    "health",
  ],
): CanonicalRouteSource => ({
  clientKey,
  contract: "verificationOperate",
  operation,
  cacheScope,
  authoritativeFor,
});

export const CANONICAL_VERIFICATION_ROUTES = [
  {
    routeId: "overview",
    path: "/",
    page: "overview",
    primary: "decide",
    label: "Overview",
    routeExport: "homeRoute",
    pageRoot: "OverviewPage",
    source: shellSource(
      "overview",
      "overview",
      ["tenant", "environment", "identity", "role"],
      ["tenant", "environment", "identity", "role", "health", "posture", "inbox-count"],
    ),
  },
  {
    routeId: "inbox",
    path: "/inbox",
    page: "inbox",
    primary: "decide",
    label: "Inbox",
    routeExport: "inboxRoute",
    pageRoot: "InboxPage",
    source: shellSource(
      "inbox",
      "queue",
      ["tenant", "environment", "identity", "role", "filters"],
      ["tenant", "environment", "identity", "role", "inbox-count"],
    ),
    urlState: ["assignedActorId", "severity", "status", "q"],
  },
  {
    routeId: "findings",
    path: "/findings",
    page: "findings",
    primary: "decide",
    label: "Findings",
    routeExport: "findingsRoute",
    pageRoot: "FindingsPage",
    source: shellSource(
      "findingsList",
      "findingsList",
      ["tenant", "environment", "identity", "role", "filters"],
      ["tenant", "environment", "identity", "role", "verdict"],
    ),
    urlState: ["controlId", "severity", "status", "assignedActorId"],
  },
  {
    routeId: "finding-detail",
    path: "/findings/$id",
    page: "finding",
    primary: "decide",
    label: "Finding detail",
    routeExport: "findingDetailRoute",
    pageRoot: "FindingDetailPage",
    source: shellSource(
      "findingsGet",
      "findingsGet",
      ["tenant", "environment", "identity", "role", "id"],
      ["tenant", "environment", "identity", "role", "verdict"],
    ),
  },
  {
    routeId: "runs",
    path: "/runs",
    page: "runs",
    primary: "decide",
    label: "Runs",
    routeExport: "runsRoute",
    pageRoot: "RunsPage",
    source: shellSource(
      "runsList",
      "runsList",
      ["tenant", "environment", "identity", "role", "filters"],
      ["tenant", "environment", "identity", "role", "receipt"],
    ),
    urlState: ["aggregate", "controlId", "lifecycle"],
  },
  {
    routeId: "run-detail",
    path: "/runs/$id",
    page: "run",
    primary: "decide",
    label: "Run detail",
    routeExport: "runDetailRoute",
    pageRoot: "RunDetailPage",
    source: shellSource(
      "runsGet",
      "runsGet",
      ["tenant", "environment", "identity", "role", "id"],
      ["tenant", "environment", "identity", "role", "receipt"],
    ),
  },
  {
    routeId: "work",
    path: "/work-ledger",
    page: "work",
    primary: "define",
    label: "Work ledger",
    routeExport: "workRoute",
    pageRoot: "WorkLedgerPage",
    source: {
      clientKey: "workLedger",
      contract: "universalVerification",
      operation: "overview",
      cacheScope: ["tenant", "environment", "identity", "role"],
      authoritativeFor: ["tenant", "identity", "role", "verdict", "receipt"],
    },
  },
  {
    routeId: "work-detail",
    path: "/work-ledger/$id",
    page: "work-detail",
    primary: "define",
    label: "Work detail",
    routeExport: "workDetailRoute",
    pageRoot: "WorkDetailPage",
    source: {
      clientKey: "workDetail",
      contract: "universalVerification",
      operation: "work",
      cacheScope: ["tenant", "environment", "identity", "role", "id"],
      authoritativeFor: ["tenant", "identity", "role", "work-detail", "verdict", "receipt"],
    },
    urlState: ["tab"],
  },
  {
    routeId: "outcomes",
    path: "/outcomes",
    page: "outcomes",
    primary: "operate",
    label: "Outcomes",
    routeExport: "outcomesRoute",
    pageRoot: "OutcomesPage",
    source: {
      clientKey: "outcomesList",
      contract: "verificationShell+verificationProve+verificationEvidence",
      operation: "runsList+receiptsList+artifactsList",
      cacheScope: ["tenant", "environment", "identity", "role", "filters"],
      authoritativeFor: ["tenant", "environment", "identity", "role", "receipt", "verdict"],
    },
    urlState: ["status", "sourceId"],
  },
  {
    routeId: "autonomy",
    path: "/autonomy",
    page: "autonomy",
    primary: "operate",
    label: "Autonomy",
    routeExport: "autonomyRoute",
    pageRoot: "AutonomyPage",
    source: {
      clientKey: "autonomyList",
      contract: "verificationDefine",
      operation: "workflowsList",
      cacheScope: ["tenant", "environment", "identity", "role", "filters"],
      authoritativeFor: ["tenant", "environment", "identity", "role", "verdict"],
    },
    urlState: ["consequence", "status"],
  },
  {
    routeId: "analytics",
    path: "/analytics",
    page: "analytics",
    primary: "operate",
    label: "Analytics",
    routeExport: "analyticsRoute",
    pageRoot: "AnalyticsPage",
    source: {
      clientKey: "analyticsGet",
      contract: "verificationShell+verificationOperate",
      operation: "overview+scorecardGet+findingsList",
      cacheScope: ["tenant", "environment", "identity", "role", "filters"],
      authoritativeFor: ["tenant", "environment", "identity", "role", "health", "verdict"],
    },
    urlState: ["range", "status"],
  },
  {
    routeId: "workflows",
    path: "/workflows",
    page: "workflows",
    primary: "define",
    label: "Workflows",
    routeExport: "workflowsRoute",
    pageRoot: "WorkflowsPage",
    source: defineSource("workflowsList", "workflowsList", [
      "tenant",
      "environment",
      "identity",
      "role",
      "filters",
    ]),
    urlState: ["ownerActorId", "status"],
  },
  {
    routeId: "workflow-new",
    path: "/workflows/new",
    page: "workflow-new",
    primary: "define",
    label: "New workflow",
    routeExport: "workflowNewRoute",
    pageRoot: "WorkflowNewPage",
    source: defineSource("workflowsList", "workflowsList", [
      "tenant",
      "environment",
      "identity",
      "role",
    ]),
  },
  {
    routeId: "workflow-detail",
    path: "/workflows/$id",
    page: "workflow",
    primary: "define",
    label: "Workflow detail",
    routeExport: "workflowDetailRoute",
    pageRoot: "WorkflowDetailPage",
    source: defineSource("workflowsGet", "workflowsGet", [
      "tenant",
      "environment",
      "identity",
      "role",
      "id",
    ]),
  },
  {
    routeId: "evidence",
    path: "/evidence",
    page: "evidence",
    primary: "evidence",
    label: "Evidence",
    routeExport: "evidenceRoute",
    pageRoot: "EvidencePage",
    source: evidenceSource("artifactsList", "artifactsList", [
      "tenant",
      "environment",
      "identity",
      "role",
      "filters",
    ]),
    urlState: ["controlId", "kind", "sourceId"],
  },
  {
    routeId: "artifact-detail",
    path: "/evidence/$id",
    page: "artifact",
    primary: "evidence",
    label: "Artifact detail",
    routeExport: "artifactDetailRoute",
    pageRoot: "ArtifactDetailPage",
    source: evidenceSource("artifactsGet", "artifactsGet", [
      "tenant",
      "environment",
      "identity",
      "role",
      "id",
    ]),
  },
  {
    routeId: "connectors",
    path: "/connectors",
    page: "connectors",
    primary: "evidence",
    label: "Connectors",
    routeExport: "connectorsRoute",
    pageRoot: "ConnectorsPage",
    source: evidenceSource("connectorsList", "connectorsList", [
      "tenant",
      "environment",
      "identity",
      "role",
    ]),
  },
  {
    routeId: "environments",
    path: "/environments",
    page: "environments",
    primary: "evidence",
    label: "Environments",
    routeExport: "environmentsRoute",
    pageRoot: "EnvironmentsPage",
    source: {
      clientKey: "environmentsList",
      contract: "environments",
      operation: "list",
      cacheScope: ["tenant", "environment", "identity", "role"],
      authoritativeFor: ["tenant", "environment", "identity", "role", "health"],
    },
  },
  {
    routeId: "controls",
    path: "/controls",
    page: "controls",
    primary: "define",
    label: "Controls",
    routeExport: "controlsRoute",
    pageRoot: "ControlsPage",
    source: defineSource("controlsList", "controlsList", [
      "tenant",
      "environment",
      "identity",
      "role",
      "filters",
    ]),
    urlState: ["ownerActorId", "status"],
  },
  {
    routeId: "control-detail",
    path: "/controls/$id",
    page: "control",
    primary: "define",
    label: "Control detail",
    routeExport: "controlDetailRoute",
    pageRoot: "ControlDetailPage",
    source: defineSource("controlsGet", "controlsGet", [
      "tenant",
      "environment",
      "identity",
      "role",
      "id",
    ]),
  },
  {
    routeId: "policies",
    path: "/policies",
    page: "policies",
    primary: "define",
    label: "Policies",
    routeExport: "policiesRoute",
    pageRoot: "PoliciesPage",
    source: defineSource("policiesList", "policiesList", [
      "tenant",
      "environment",
      "identity",
      "role",
      "filters",
    ]),
    urlState: ["controlId", "status"],
  },
  {
    routeId: "policy-detail",
    path: "/policies/$id",
    page: "policy",
    primary: "define",
    label: "Policy detail",
    routeExport: "policyDetailRoute",
    pageRoot: "PolicyDetailPage",
    source: defineSource("policiesGet", "policiesGet", [
      "tenant",
      "environment",
      "identity",
      "role",
      "id",
    ]),
  },
  {
    routeId: "workers",
    path: "/workers",
    page: "workers",
    primary: "operate",
    label: "Workers",
    routeExport: "workersRoute",
    pageRoot: "WorkersPage",
    source: operateSource("workersList", "workersList", [
      "tenant",
      "environment",
      "identity",
      "role",
    ]),
  },
  {
    routeId: "scorecard",
    path: "/scorecard",
    page: "scorecard",
    primary: "operate",
    label: "Scorecard",
    routeExport: "scorecardRoute",
    pageRoot: "ScorecardPage",
    source: operateSource(
      "scorecardGet",
      "scorecardGet",
      ["tenant", "environment", "identity", "role"],
      ["tenant", "environment", "identity", "role", "health", "posture"],
    ),
  },
  {
    routeId: "runtime",
    path: "/runtime",
    page: "runtime",
    primary: "operate",
    label: "Runtime",
    routeExport: "runtimeRoute",
    pageRoot: "RuntimePage",
    source: operateSource("runtimeGet", "runtimeGet", [
      "tenant",
      "environment",
      "identity",
      "role",
    ]),
  },
  {
    routeId: "api",
    path: "/sdk",
    page: "api",
    primary: "operate",
    label: "API & SDK",
    routeExport: "sdkRoute",
    pageRoot: "ApiPage",
    source: operateSource("catalogGet", "catalogGet", [
      "tenant",
      "environment",
      "identity",
      "role",
    ]),
  },
  {
    routeId: "demo-lab",
    path: "/demo-lab",
    page: "demo-lab",
    primary: "operate",
    label: "Demo lab",
    routeExport: "demoLabRoute",
    pageRoot: "DemoLabPage",
    source: operateSource(
      "demoLab",
      "stateTransitionAudit",
      ["tenant", "environment", "identity", "role"],
      ["tenant", "environment", "identity", "role", "health", "posture"],
    ),
  },
  {
    routeId: "trust",
    path: "/trust",
    page: "trust",
    primary: "operate",
    label: "Trust root",
    routeExport: "trustRoute",
    pageRoot: "TrustPage",
    source: operateSource(
      "runtimeGet",
      "runtimeGet",
      ["tenant", "environment", "identity", "role"],
      ["tenant", "environment", "identity", "role", "health", "receipt"],
    ),
  },
  {
    routeId: "posture",
    path: "/posture",
    page: "posture",
    primary: "operate",
    label: "Posture",
    routeExport: "postureRoute",
    pageRoot: "PosturePage",
    source: operateSource(
      "scorecardGet",
      "scorecardGet",
      ["tenant", "environment", "identity", "role"],
      ["tenant", "environment", "identity", "role", "posture"],
    ),
  },
  {
    routeId: "receipts",
    path: "/receipts",
    page: "receipts",
    primary: "prove",
    label: "Receipts",
    routeExport: "receiptsRoute",
    pageRoot: "ReceiptsPage",
    source: proveSource("receiptsList", "receiptsList", [
      "tenant",
      "environment",
      "identity",
      "role",
      "filters",
    ]),
    urlState: ["controlId", "status"],
  },
  {
    routeId: "receipt-detail",
    path: "/receipts/$id",
    page: "receipt",
    primary: "prove",
    label: "Receipt detail",
    routeExport: "receiptDetailRoute",
    pageRoot: "ReceiptDetailPage",
    source: proveSource("receiptsGet", "receiptsGet", [
      "tenant",
      "environment",
      "identity",
      "role",
      "id",
    ]),
  },
  {
    routeId: "packs",
    path: "/packs",
    page: "packs",
    primary: "prove",
    label: "Evidence packs",
    routeExport: "packsRoute",
    pageRoot: "PacksPage",
    source: proveSource("packsList", "packsList", [
      "tenant",
      "environment",
      "identity",
      "role",
      "filters",
    ]),
    urlState: ["framework", "status"],
  },
  {
    routeId: "pack-detail",
    path: "/packs/$id",
    page: "pack",
    primary: "prove",
    label: "Evidence pack detail",
    routeExport: "packDetailRoute",
    pageRoot: "PackDetailPage",
    source: proveSource("packsGet", "packsGet", [
      "tenant",
      "environment",
      "identity",
      "role",
      "id",
    ]),
  },
  {
    routeId: "audit-room",
    path: "/audit-room",
    page: "audit-room",
    primary: "prove",
    label: "Audit room",
    routeExport: "auditRoomRoute",
    pageRoot: "AuditRoomPage",
    source: proveSource("auditRoomGet", "auditRoomGet", [
      "tenant",
      "environment",
      "identity",
      "role",
    ]),
  },
] as const satisfies readonly CanonicalRoute[];

export const CANONICAL_ROUTE_EXPORTS = CANONICAL_VERIFICATION_ROUTES.map(
  (route) => route.routeExport,
);
export const CANONICAL_ROUTE_PATHS = CANONICAL_VERIFICATION_ROUTES.map((route) => route.path);
